import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, mutate } from '../shared/api';
import { Icon } from './kycCharts';

/**
 * m.72 - katalog indikatora (Irenina tocka 7 zahtjeva / tocka 13 primjedbi).
 *
 * Do sada se 110 indikatora iz Priloga III moglo samo citati. Ovlastenik nije
 * mogao ugasiti indikator koji se pokazao neupotrebljivim, promijeniti mu
 * tezinu ni dodati svoj - za sve je trebala migracija.
 */

type Indicator = {
    id: number;
    code: string;
    name: string;
    category: string | null;
    description: string | null;
    severity: 'medium' | 'high';
    weight: number;
    automatic: boolean | number;
    active: boolean | number;
    note: string | null;
    updated_by_name: string | null;
    updated_at: string | null;
    hit_count: number;
};

type CatalogResponse = {
    rows: Indicator[];
    categories: string[];
    totals: { all: number; active: number };
};

const INK = '#23405f';
const card: React.CSSProperties = { background: '#fff', border: '1px solid #d8e2ec', borderRadius: 12, padding: 16, marginBottom: 14 };
const INP: React.CSSProperties = { padding: '8px 10px', border: '1px solid #ccd6e0', borderRadius: 8, fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' };
const BTN: React.CSSProperties = { padding: '9px 15px', background: INK, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.86rem' };
const smallBtn: React.CSSProperties = { ...BTN, padding: '5px 9px', fontSize: '0.75rem' };
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #d8e2ec', color: '#4a6785', fontWeight: 800, fontSize: '0.71rem', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #eef3f8', fontSize: '0.8rem', verticalAlign: 'top' };

const SEVERITY_LABEL: Record<string, string> = { medium: 'Srednji', high: 'Visok' };

const EMPTY_NEW = { code: '', name: '', category: '', description: '', severity: 'medium', weight: '10', note: '' };

export function IndicatorCatalog({ token }: { token: string }) {
    const [data, setData] = useState<CatalogResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState({ category: '', severity: '', active: '', search: '' });
    const [editing, setEditing] = useState<Record<number, Partial<Indicator>>>({});
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ ...EMPTY_NEW });
    const [busy, setBusy] = useState<number | 'new' | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v.trim() !== '') params.set(k, v.trim()); });
        api<CatalogResponse>(`/kyc/compliance/indicator-catalog?${params.toString()}`, token)
            .then((r) => { setData(r); setEditing({}); })
            .catch((e: Error) => setError(e.message))
            .finally(() => setLoading(false));
    }, [filters, token]);

    useEffect(() => { load(); }, [load]);

    const set = (key: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setFilters((f) => ({ ...f, [key]: e.target.value }));

    const patch = async (id: number, body: Record<string, unknown>) => {
        setBusy(id);
        try {
            await mutate(`/kyc/compliance/indicator-catalog/${id}`, token, 'PATCH', body);
            load();
        } catch (e) {
            alert(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    };

    const create = async () => {
        setBusy('new');
        try {
            await mutate('/kyc/compliance/indicator-catalog', token, 'POST', {
                ...draft,
                weight: draft.weight === '' ? null : Number(draft.weight),
                code: draft.code || null,
            });
            setDraft({ ...EMPTY_NEW });
            setCreating(false);
            load();
        } catch (e) {
            alert(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    };

    const grouped = useMemo(() => {
        const map = new Map<string, Indicator[]>();
        for (const row of data?.rows ?? []) {
            const key = row.category ?? 'Ostalo';
            map.set(key, [...(map.get(key) ?? []), row]);
        }
        return [...map.entries()];
    }, [data]);

    return (
        <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: INK, background: '#eef3f8', borderRadius: 8, padding: 6, display: 'inline-flex' }}><Icon name="flag" size={17} /></span>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 780, color: INK }}>Katalog indikatora</div>
                    <div style={{ fontSize: 10.8, color: '#8794a5', marginTop: 1 }}>
                        {data ? `${data.totals.active} aktivnih od ${data.totals.all}` : 'Prilog III i vlastiti indikatori'}
                    </div>
                </div>
                <button type="button" style={BTN} onClick={() => setCreating((v) => !v)}>
                    {creating ? 'Odustani' : '➕ Novi indikator'}
                </button>
            </div>

            {creating && (
                <div style={{ marginTop: 12, padding: 12, background: '#f7fafc', border: '1px solid #d8e2ec', borderRadius: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                        <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                            Naziv *
                            <input style={INP} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                        </label>
                        <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                            Kategorija *
                            <input style={INP} list="indicator-categories" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
                            <datalist id="indicator-categories">{(data?.categories ?? []).map((c) => <option key={c} value={c} />)}</datalist>
                        </label>
                        <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                            Ozbiljnost
                            <select style={INP} value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value })}>
                                <option value="medium">Srednji</option><option value="high">Visok</option>
                            </select>
                        </label>
                        <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                            Težina (0-100)
                            <input style={INP} type="number" min="0" max="100" value={draft.weight} onChange={(e) => setDraft({ ...draft, weight: e.target.value })} />
                        </label>
                        <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                            Šifra (nije obavezna)
                            <input style={INP} value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="izvodi se iz naziva" />
                        </label>
                    </div>
                    <textarea style={{ ...INP, minHeight: 56, marginTop: 8 }} placeholder="Opis indikatora"
                        value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                    <button type="button" style={{ ...BTN, marginTop: 8 }} disabled={busy === 'new' || !draft.name || !draft.category} onClick={() => void create()}>
                        {busy === 'new' ? '…' : 'Spremi indikator'}
                    </button>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginTop: 12 }}>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Kategorija
                    <select style={INP} value={filters.category} onChange={set('category')}>
                        <option value="">sve</option>
                        {(data?.categories ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Ozbiljnost
                    <select style={INP} value={filters.severity} onChange={set('severity')}>
                        <option value="">sve</option><option value="medium">Srednji</option><option value="high">Visok</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Stanje
                    <select style={INP} value={filters.active} onChange={set('active')}>
                        <option value="">sve</option><option value="1">aktivni</option><option value="0">ugašeni</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.74rem', color: '#4a6785', fontWeight: 700 }}>
                    Pretraga
                    <input style={INP} value={filters.search} onChange={set('search')} placeholder="naziv, šifra, opis" />
                </label>
            </div>

            {loading && <div style={{ marginTop: 12, fontSize: '0.84rem', color: '#8794a5' }}>Učitavam…</div>}
            {error && <div style={{ marginTop: 12, color: '#bb1111', fontSize: '0.84rem' }}>{error}</div>}

            {grouped.map(([category, rows]) => (
                <div key={category} style={{ marginTop: 16 }}>
                    <div style={{ color: '#4a6785', fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        {category} <span style={{ color: '#8794a5', fontWeight: 600 }}>({rows.length})</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={th}>Indikator</th>
                                    <th style={th}>Ozbiljnost</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Težina</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Označen</th>
                                    <th style={th}>Bilješka</th>
                                    <th style={th}>Stanje</th>
                                    <th style={th} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const e = editing[r.id] ?? {};
                                    const dirty = Object.keys(e).length > 0;
                                    return (
                                        <tr key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                                            <td style={td}>
                                                <div style={{ fontWeight: 700 }}>{r.name}</div>
                                                <div style={{ fontSize: '0.7rem', color: '#8794a5' }}>
                                                    {r.code}{r.automatic ? ' · automatski' : ''}
                                                    {r.updated_by_name ? ` · zadnji uredio: ${r.updated_by_name}` : ''}
                                                </div>
                                                {r.description && <div style={{ fontSize: '0.74rem', color: '#5a6b7d', marginTop: 3 }}>{r.description}</div>}
                                            </td>
                                            <td style={td}>
                                                <select style={{ ...INP, padding: '4px 6px', fontSize: '0.78rem' }}
                                                    value={(e.severity ?? r.severity) as string}
                                                    onChange={(ev) => setEditing({ ...editing, [r.id]: { ...e, severity: ev.target.value as 'medium' | 'high' } })}>
                                                    {Object.entries(SEVERITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>
                                                <input type="number" min="0" max="100" style={{ ...INP, padding: '4px 6px', fontSize: '0.78rem', width: 62, textAlign: 'right' }}
                                                    value={String(e.weight ?? r.weight)}
                                                    onChange={(ev) => setEditing({ ...editing, [r.id]: { ...e, weight: Number(ev.target.value) } })} />
                                            </td>
                                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.hit_count}x</td>
                                            <td style={td}>
                                                <input style={{ ...INP, padding: '4px 6px', fontSize: '0.78rem' }}
                                                    value={(e.note ?? r.note ?? '') as string}
                                                    placeholder="—"
                                                    onChange={(ev) => setEditing({ ...editing, [r.id]: { ...e, note: ev.target.value } })} />
                                            </td>
                                            <td style={td}>
                                                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                                                    <input type="checkbox" checked={Boolean(e.active ?? r.active)}
                                                        onChange={(ev) => setEditing({ ...editing, [r.id]: { ...e, active: ev.target.checked } })} />
                                                    {(e.active ?? r.active) ? 'aktivan' : 'ugašen'}
                                                </label>
                                            </td>
                                            <td style={td}>
                                                {dirty && (
                                                    <button type="button" style={smallBtn} disabled={busy === r.id}
                                                        onClick={() => void patch(r.id, e as Record<string, unknown>)}>
                                                        {busy === r.id ? '…' : 'Spremi'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {!loading && grouped.length === 0 && !error && (
                <div style={{ marginTop: 12, fontSize: '0.84rem', color: '#8794a5' }}>Nema indikatora za zadane filtre.</div>
            )}

            <div style={{ marginTop: 14, fontSize: '0.74rem', color: '#8794a5' }}>
                Indikator se ne briše nego gasi. Postojeći pogodci pokazuju na njega i čine dio
                zaključenih dosjea; brisanjem bi dosje ostao bez obrazloženja zašto je predmet označen.
            </div>
        </div>
    );
}
